﻿import { OptionDropdown } from "@/components";
import SpinLoader from "@/containers/Layout/MainLayout/SpinLoader";
import { editableComponents } from "@/containers/ProjectTemplate";
import { useMilestoneRadioProperty } from "@/containers/ProjectTemplate/components/TableElements/hooks";
import {
  clearTargetDatePrepopulationSource,
  deleteMilestone,
  duplicateMilestone,
  recursivelyUpdateMilestoneRadioProperty,
  setUniqueDefaultFor,
  updateMilestoneRecursively,
} from "@/containers/ProjectTemplate/components/helpers";
import { findMissingProductCategoriesForRequiredApprovers } from "@/containers/ProjectTemplate/helpers";
import { FieldType as EnumFieldType, FormType, MenuItemType } from "@/enums";
import { flattenMilestones, nameof } from "@/helpers";
import { generateMenuItems } from "@/helpers/generateMenuItems";
import {
  IFormFieldOptions,
  IMilestoneTemplate,
  ITranslation,
  IViperMenuItem,
} from "@/interfaces";
import { useFormPropertyProvider } from "@/providers";
import { useGetFormLookupsQuery } from "@/services";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCaretDown,
  faCaretRight,
  faEdit,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, Radio, Table, Tooltip } from "antd";
import { ColumnType } from "antd/es/table";
import _ from "lodash";
import { MenuInfo } from "rc-menu/lib/interface";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { v4 } from "uuid";
import { ModalNameType } from "../../types";
import { findTargetGeneration } from "../helpers/findTargetGeneration";
import * as S from "./styled";

type MenuOptionKeyType =
  | "editing"
  | "editing:name"
  | "editing:delete"
  | "milestones"
  | "milestones:addBefore"
  | "milestones:addAfter"
  | "milestones:addChild"
  | "milestones:duplicate";

interface IProps {
  expandedRows: string[];
  showActive: boolean;
  milestoneTemplates: IMilestoneTemplate[];
  setExpandedRows: (rows: string[]) => void;
  setMilestoneTemplates: Dispatch<SetStateAction<IMilestoneTemplate[]>>;
  toggleModal: (
    name: ModalNameType,
    milestoneTemplates?: IMilestoneTemplate
  ) => void;
  isLoading?: boolean;
  onToggleKeyMilestone?: (milestoneId: string, checked: boolean) => void;
}

export const MilestoneTemplateTable = ({
  showActive,
  milestoneTemplates,
  expandedRows,
  setExpandedRows,
  setMilestoneTemplates,
  toggleModal,
  onToggleKeyMilestone,
}: IProps) => {
  const intl = useIntl();
  const [dateFields, setDateFields] = useState<IFormFieldOptions>();
  const [isLoading, setIsLoading] = useState(true);
  const [approvalRequiredErrorRows, setApprovalRequiredErrorRows] = useState<
    string[]
  >([]);
  const flattenedMilestones = useMemo(
    () => flattenMilestones(milestoneTemplates),

    [milestoneTemplates]
  );

  const shouldProjectCompleteMilestoneId = useMilestoneRadioProperty(
    flattenedMilestones,
    "shouldCompleteProject"
  );

  const targetDatesRequiredMilestoneId = useMilestoneRadioProperty(
    flattenedMilestones,
    "targetDatesRequired"
  );
  const areProjectSkusRequiredMilestoneId = useMilestoneRadioProperty(
    flattenedMilestones,
    "areProjectSkusRequired"
  );

  const { data: { intake } = {} } = useGetFormLookupsQuery();
  const { getFormProperties, getAllFormProperties } = useFormPropertyProvider();

  useEffect(() => {
    if (!intake) return;

    const formProperties = getAllFormProperties(FormType.Intake);
    const intakeDateFields = formProperties.filter(
      (property: { fieldType: EnumFieldType }) =>
        property?.fieldType === EnumFieldType.Date ?? false
    );

    if (!intakeDateFields?.length) {
      setIsLoading(false);
      return;
    }

    const formFieldOptions: IFormFieldOptions = {};

    intakeDateFields.forEach(
      ({
        label: { fallback, id },
        name,
      }: {
        label: ITranslation;
        name: string;
      }) => {
        formFieldOptions[name] = {
          id,
          fallback: intl.formatMessage({
            id,
            defaultMessage: fallback,
          }),
        };
      }
    );

    formFieldOptions[""] = {
      id: "",
      fallback: intl.formatMessage({ id: "general.notApplicable" }),
    };
    setDateFields(formFieldOptions);
    setIsLoading(false);
  }, [getAllFormProperties, getFormProperties, intl, intake]);

  const updateExpandedRows = (row: IMilestoneTemplate): void => {
    const id = row?.id;
    const isExpanded: boolean = _.indexOf(expandedRows, id) > -1;

    isExpanded
      ? setExpandedRows(expandedRows.filter((r): boolean => r !== id))
      : setExpandedRows([...expandedRows, row.id]);
  };

  const handleUpdate = useCallback(
    (row: IMilestoneTemplate): void => {
      let updateMilestoneTemplates = updateMilestoneRecursively(
        row,
        milestoneTemplates
      );

      const radioProperties: (keyof IMilestoneTemplate)[] = [
        "areProjectSkusRequired",
        "targetDatesRequired",
        "shouldCompleteProject",
      ];

      radioProperties.forEach((property) => {
        updateMilestoneTemplates = recursivelyUpdateMilestoneRadioProperty(
          updateMilestoneTemplates,
          row,
          property
        );
      });

      if (row.targetDatesRequired) {
        updateMilestoneTemplates = setUniqueDefaultFor(
          row.id,
          updateMilestoneTemplates
        );
      }

      if (row.targetDatePrepopulationSource !== "") {
        updateMilestoneTemplates = clearTargetDatePrepopulationSource(
          row.id,
          updateMilestoneTemplates
        );
      }

      setMilestoneTemplates(updateMilestoneTemplates);
    },
    [milestoneTemplates, setMilestoneTemplates]
  );

  const validateApprovalRequired = useCallback(
    (row: IMilestoneTemplate, isActive: boolean): void => {
      const id = row?.id;
      const errorExists: boolean =
        _.indexOf(approvalRequiredErrorRows, id) > -1;

      if (!isActive) {
        if (errorExists) {
          setApprovalRequiredErrorRows(
            approvalRequiredErrorRows.filter((r): boolean => r !== id)
          );
        }
        return;
      }

      const missingApproversForProductCategories =
        findMissingProductCategoriesForRequiredApprovers(row);
      if (missingApproversForProductCategories.length > 0) {
        if (!errorExists) {
          setApprovalRequiredErrorRows([...approvalRequiredErrorRows, row.id]);
        }
        toast.error(
          intl.formatMessage({
            id: "milestoneTemplates.columns.approversNeeded",
          })
        );
        handleUpdate({ ...row, isActive: false });
        return;
      } else if (errorExists) {
        setApprovalRequiredErrorRows(
          approvalRequiredErrorRows.filter((r): boolean => r !== id)
        );
      }
    },
    [
      approvalRequiredErrorRows,
      setApprovalRequiredErrorRows,
      handleUpdate,
      intl,
    ]
  );

  const generateActionItems = useCallback(
    (record: IMilestoneTemplate): IViperMenuItem<MenuOptionKeyType>[] => {
      const canAddChild =
        findTargetGeneration(milestoneTemplates, record) < 4 ?? false;

      const actionItems: IViperMenuItem<MenuOptionKeyType>[] = [
        {
          type: MenuItemType.Group,
          key: "editing",
          title: intl.formatMessage({ id: "milestoneTemplates.actions.edit" }),
        },
        {
          type: MenuItemType.Item,
          key: "editing:name",
          title: intl.formatMessage({
            id: "milestoneTemplates.actions.editName",
          }),
        },
        {
          type: MenuItemType.Item,
          key: "editing:delete",
          title: intl.formatMessage({
            id: "milestoneTemplates.actions.delete",
          }),
        },
        {
          type: MenuItemType.Group,
          key: "milestones",
          title: intl.formatMessage({
            id: "milestoneTemplates.actions.milestones",
          }),
        },
        {
          type: MenuItemType.Item,
          key: "milestones:addBefore",
          title: intl.formatMessage({
            id: "milestoneTemplates.actions.addBefore",
          }),
        },
        {
          type: MenuItemType.Item,
          key: "milestones:addAfter",
          title: intl.formatMessage({
            id: "milestoneTemplates.actions.addAfter",
          }),
        },
      ];

      if (canAddChild) {
        actionItems.push({
          type: MenuItemType.Item,
          key: "milestones:addChild",
          title: intl.formatMessage({
            id: "milestoneTemplates.actions.addChild",
          }),
        });
      }

      actionItems.push({
        type: MenuItemType.Item,
        key: "milestones:duplicate",
        title: intl.formatMessage({
          id: "milestoneTemplates.actions.duplicate",
        }),
      });

      return actionItems;
    },
    [milestoneTemplates, intl]
  );

  const handleMenuActions = useCallback(
    (event: MenuInfo, milestoneTemplate: IMilestoneTemplate): void => {
      const key = event.key as MenuOptionKeyType;

      switch (key) {
        case "editing:name":
          toggleModal("name", milestoneTemplate);
          break;
        case "milestones:duplicate":
          setMilestoneTemplates((value): IMilestoneTemplate[] =>
            duplicateMilestone(value, milestoneTemplate, v4)
          );
          break;
        case "editing:delete":
          setMilestoneTemplates((value): IMilestoneTemplate[] =>
            deleteMilestone(value, milestoneTemplate)
          );
          break;
        case "milestones:addChild":
          toggleModal("addChild", milestoneTemplate);
          break;
        case "milestones:addBefore":
          toggleModal("addBefore", milestoneTemplate);
          break;
        case "milestones:addAfter":
          toggleModal("addAfter", milestoneTemplate);
          break;
        default:
          console.error(`${key} action not defined.`);
          break;
      }
    },
    [toggleModal, setMilestoneTemplates]
  );

  const defaultColumns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: "milestoneTemplates.columns.name" }),
        dataIndex: nameof<IMilestoneTemplate>("name"),
        key: "name",
        width: "250px",
        type: EnumFieldType.Text,
        editable: true,
      },
      {
        title: intl.formatMessage({
          id: "milestoneTemplates.columns.abbreviation",
        }),
        dataIndex: nameof<IMilestoneTemplate>("abbreviation"),
        key: "abbreviation",
        width: "65px",
        type: EnumFieldType.Text,
        editable: true,
      },
      {
        title: "",
        dataIndex: "",
        key: "edit",
        width: "55px",
        type: undefined,
        render: (record: IMilestoneTemplate) => (
          <span>
            <Button
              data-test-id={`milestone-template-edit-${record}`}
              type="link"
              onClick={(): void => {
                toggleModal("template", record);
              }}
              icon={<FontAwesomeIcon icon={faEdit as IconProp} />}
            />
            {approvalRequiredErrorRows.find(
              (id: string) => id === record.id
            ) && (
              <Tooltip
                title={intl.formatMessage({
                  id: "milestoneTemplates.columns.approversNeeded",
                })}
                trigger={["hover"]}
              >
                <FontAwesomeIcon
                  data-test-id={`milestone-template-edit-${record}`}
                  icon={faTriangleExclamation as IconProp}
                  color="red"
                />
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.document",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.document" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("documentsRequired"),
        key: "documentsRequired",
        width: "50px",
        type: EnumFieldType.Checkbox,
        editable: true,
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.approvalRequired",
            })}
          >
            {intl.formatMessage({
              id: "milestoneTemplates.columns.approvalRequired",
            })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("approvalRequired"),
        key: "approvalRequired",
        width: "150px",
        type: EnumFieldType.CheckboxMultiSelectDropDown,
        editable: true,
        options: intake?.productCategory || {},
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.sku",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.sku" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("areProjectSkusRequired"),
        key: "areProjectSkusRequired",
        width: "50px",
        type: undefined,
        align: "center",
        editable: true,
        render: function (_: never, record: IMilestoneTemplate) {
          const milestone = record as IMilestoneTemplate;
          return (
            <Checkbox
              className="m-0"
              data-test-id={`skus-required-${milestone.id}`}
              checked={areProjectSkusRequiredMilestoneId === milestone.id}
              onChange={(e): void => {
                handleUpdate({
                  ...milestone,
                  areProjectSkusRequired: e.target.checked,
                });
              }}
            />
          );
        },
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.date",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.date" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("targetDatesRequired"),
        key: "date",
        width: "55px",
        type: undefined,
        align: "center",
        editable: true,
        render: (_: never, record: IMilestoneTemplate) => {
          const milestone = record as IMilestoneTemplate;
          return (
            <Checkbox
              className="m-0"
              data-test-id={`target-dates-required-${milestone.id}`}
              checked={targetDatesRequiredMilestoneId === milestone.id}
              onChange={(e): void => {
                handleUpdate({
                  ...milestone,
                  targetDatesRequired: e.target.checked,
                });
              }}
            />
          );
        },
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.complete",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.complete" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("shouldCompleteProject"),
        key: "shouldCompleteProject",
        width: "85px",
        type: undefined,
        align: "center",
        editable: true,
        render: (_: never, record: IMilestoneTemplate) => {
          const milestone = record as IMilestoneTemplate;
          return (
            <Radio
              className="m-0"
              data-test-id={`should-complete-project-radio-${milestone.id}`}
              checked={shouldProjectCompleteMilestoneId === milestone.id}
              onClick={(): void => {
                handleUpdate({ ...milestone, shouldCompleteProject: true });
              }}
            />
          );
        },
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.secure",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.secure" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("secureProjectOnCompletion"),
        key: "secure",
        width: "70px",
        type: EnumFieldType.Checkbox,
        editable: true,
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.active",
            })}
          >
            {intl.formatMessage({ id: "general.active" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("isActive"),
        key: "isActive",
        width: "70px",
        type: undefined,
        align: "center",
        editable: true,
        render: (_: never, record: IMilestoneTemplate) => {
          const milestone = record as IMilestoneTemplate;
          return (
            <Checkbox
              className="m-0"
              data-test-id={`is-active-${milestone.id}`}
              checked={milestone.isActive}
              onChange={(e): void => {
                handleUpdate({ ...milestone, isActive: e.target.checked });
                validateApprovalRequired({ ...milestone }, e.target.checked);
              }}
            />
          );
        },
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.keyMilestone",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.keyMilestone" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("isKeyMilestone"),
        key: "isKeyMilestone",
        width: "110px",
        type: undefined,
        align: "center",
        editable: true,
        render: (_: never, record: IMilestoneTemplate) => {
          const milestone = record as IMilestoneTemplate;
          return (
            <Checkbox
              className="m-0"
              data-test-id={`is-key-milestone-${milestone.id}`}
              checked={milestone.isKeyMilestone}
              onChange={(e): void => {
                handleUpdate({
                  ...milestone,
                  isKeyMilestone: e.target.checked,
                });
                onToggleKeyMilestone?.(milestone.id, e.target.checked);
              }}
            />
          );
        },
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.prepopulateDate",
            })}
          >
            {intl.formatMessage({
              id: "milestoneTemplates.columns.prepopulateDate",
            })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("targetDatePrepopulationSource"),
        key: "prepopulationSource",
        width: "175px",
        type: EnumFieldType.DropDown,
        editable: true,
        options: dateFields,
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.default",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.default" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("defaultFor"),
        key: "default",
        width: "150px",
        type: EnumFieldType.CheckboxMultiSelectDropDown,
        editable: true,
        options: intake?.productCategory || {},
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.canAdd",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.canAdd" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("canBeAdded"),
        key: "canBeAdded",
        width: "150px",
        type: EnumFieldType.CheckboxMultiSelectDropDown,
        editable: true,
        options: intake?.productCategory || {},
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.canDelete",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.canDelete" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("canBeDeleted"),
        key: "canBeDeleted",
        width: "150px",
        type: EnumFieldType.CheckboxMultiSelectDropDown,
        editable: true,
        options: intake?.productCategory || {},
      },
      {
        title: (
          <Tooltip
            title={intl.formatMessage({
              id: "milestoneTemplates.columns.descriptions.canSkip",
            })}
          >
            {intl.formatMessage({ id: "milestoneTemplates.columns.canSkip" })}
          </Tooltip>
        ),
        dataIndex: nameof<IMilestoneTemplate>("canBeSkipped"),
        key: "canBeSkipped",
        width: "150px",
        type: EnumFieldType.CheckboxMultiSelectDropDown,
        editable: true,
        options: intake?.productCategory || {},
      },
      {
        title: "",
        dataIndex: "",
        key: "x",
        width: "150px",
        type: undefined,
        render: (record: IMilestoneTemplate) => (
          <OptionDropdown
            id="milestone-template-actions"
            items={generateMenuItems(generateActionItems(record))}
            handleMenuClick={(e): void => handleMenuActions(e, record)}
          />
        ),
      },
    ],
    [
      intl,
      dateFields,
      intake,
      approvalRequiredErrorRows,
      shouldProjectCompleteMilestoneId,
      targetDatesRequiredMilestoneId,
      areProjectSkusRequiredMilestoneId,
      handleUpdate,
      validateApprovalRequired,
      toggleModal,
      generateActionItems,
      handleMenuActions,
    ]
  );

  const columns = useMemo(
    () =>
      defaultColumns.map((col) => {
        if (!col.editable) return col;
        return {
          ...col,
          onCell: (record: IMilestoneTemplate) => ({
            record,
            disabled:
              col.dataIndex ===
                nameof<IMilestoneTemplate>("secureProjectOnCompletion") &&
              !record.shouldCompleteProject,
            editable: col.editable,
            dataIndex: col.dataIndex,
            title: col.title,
            type: col.type,
            options: col.options,
            handleSave: handleUpdate,
          }),
        };
      }),
    [defaultColumns, handleUpdate]
  );

  return (
    <Table
      className="viper-nested-table"
      rowClassName={(record): string =>
        showActive && !record.isActive ? "table-row-hidden" : "table-row"
      }
      showHeader
      size="small"
      columns={columns as ColumnType<IMilestoneTemplate>[]}
      components={editableComponents}
      expandable={{
        childrenColumnName: nameof<IMilestoneTemplate>("childMilestones"),
        indentSize: 48,
        expandedRowKeys: expandedRows,
        expandIcon: ({ expanded, record }) => {
          const { id, childMilestones } = record;

          if (!childMilestones?.length) return <span id={id} />;

          return (
            <S.ExpandChildrenButton
              type="link"
              data-test-id={`milestone-template-${
                expanded ? "collapse" : "expand"
              }-${id}`}
              icon={
                <FontAwesomeIcon
                  icon={(expanded ? faCaretDown : faCaretRight) as IconProp}
                />
              }
              onClick={(): void => updateExpandedRows(record)}
              id={id}
            />
          );
        },
      }}
      tableLayout="fixed"
      rowKey={(record: IMilestoneTemplate): string => record.id}
      dataSource={milestoneTemplates}
      loading={{ indicator: <SpinLoader />, spinning: isLoading }}
      scroll={{ y: 700 }}
      pagination={false}
      style={{ width: "100%" }}
    />
  );
};
